import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import Select from 'react-select'
import Navbar from '../../components/navbar/navbar'
import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  FiletypePdf,
  TrashFill,
  XCircleFill,
} from 'react-bootstrap-icons'
import { CustomButton } from '../../components/CustomButton'
import { CustomSpinner } from '../../components/CustomSpinner'
import ReactPaginate from 'react-paginate'
import { useOrderTable } from '../../hooks/useOrderTable'
import { toast } from 'react-toastify'

import HistoricoTransferenciaService from '../../services/database/HistoricoTransferenciaService'
import EmpresasDatabaseService from '../../services/database/EmpresasDatabaseService'

import { formatPrice } from '../../utils/formatPrice'
import { formatDate } from '../../utils/formatDate'

import noFoundItemsImg from '../../assets/illustration/no-found-items.svg'
import {
  startOfDay,
  isWithinInterval,
  parseISO,
  isValid,
  isAfter,
  format,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'

// npm install jspdf jspdf-autotable
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

/* ──────────────────────────── Helpers ────────────────────────────────────── */
function buildPeriodoLabel(startDate, endDate) {
  if (startDate && endDate)
    return `${format(parseISO(startDate), 'dd/MM/yyyy', { locale: ptBR })} até ${format(parseISO(endDate), 'dd/MM/yyyy', { locale: ptBR })}`
  if (startDate)
    return `A partir de ${format(parseISO(startDate), 'dd/MM/yyyy', { locale: ptBR })}`
  if (endDate)
    return `Até ${format(parseISO(endDate), 'dd/MM/yyyy', { locale: ptBR })}`
  return 'Todos os períodos'
}

/* ──────────────────── Geração do PDF com jsPDF + autoTable ──────────────── */
function gerarRelatorioPDF({ transferencias, filtrosAtivos, periodoLabel }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

  const agora = format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
  const azul = [26, 111, 181]       // #1a6fb5
  const verde = [25, 135, 84]       // #198754
  const vermelho = [220, 53, 69]    // #dc3545
  const cinzaClaro = [245, 248, 255]
  const cinzaTexto = [100, 100, 100]

  const pageW = doc.internal.pageSize.getWidth()

  /* ── Cabeçalho ── */
  doc.setFillColor(...azul)
  doc.rect(0, 0, pageW, 22, 'F')

  doc.setTextColor(255, 255, 255)
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.text('Relatório de Transferências', 14, 10)

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text(`Período: ${periodoLabel}`, 14, 17)
  doc.text(`Gerado em: ${agora}`, pageW - 14, 17, { align: 'right' })

  if (filtrosAtivos.length > 0) {
    doc.text(`Filtros: ${filtrosAtivos.join('  ·  ')}`, pageW - 14, 10, { align: 'right' })
  }

  /* ── Cards de resumo ── */
  const entradas = transferencias.filter((t) => t.tipo === 'entrada')
  const saidas = transferencias.filter((t) => t.tipo === 'saida')
  const totalCusto = transferencias.reduce((a, t) => a + (t.precocusto || 0), 0)
  const totalQtd = transferencias.reduce((a, t) => a + (t.quantidade || 0), 0)
  const totalEntradas = entradas.reduce((a, t) => a + (t.precocusto || 0), 0)
  const totalSaidas = saidas.reduce((a, t) => a + (t.precocusto || 0), 0)

  const cards = [
    { label: 'Registros', value: String(transferencias.length), cor: azul },
    { label: 'Qtd. Total', value: String(totalQtd), cor: [111, 66, 193] },
    { label: 'Entradas', value: `${entradas.length}  |  ${formatPrice(totalEntradas)}`, cor: verde },
    { label: 'Saídas', value: `${saidas.length}  |  ${formatPrice(totalSaidas)}`, cor: vermelho },
    { label: 'Custo Total', value: formatPrice(totalCusto), cor: [30, 30, 30] },
  ]

  const cardW = (pageW - 28 - 12) / cards.length
  const cardY = 26

  cards.forEach((card, i) => {
    const x = 14 + i * (cardW + 3)

    doc.setDrawColor(...card.cor)
    doc.setLineWidth(0.8)
    doc.setFillColor(...cinzaClaro)
    doc.roundedRect(x, cardY, cardW, 16, 2, 2, 'FD')

    // barra superior colorida
    doc.setFillColor(...card.cor)
    doc.roundedRect(x, cardY, cardW, 3, 2, 2, 'F')
    doc.rect(x, cardY + 1, cardW, 2, 'F') // "tampão" pra não arredondar embaixo

    doc.setTextColor(...cinzaTexto)
    doc.setFontSize(6.5)
    doc.setFont('helvetica', 'normal')
    doc.text(card.label.toUpperCase(), x + cardW / 2, cardY + 7.5, { align: 'center' })

    doc.setTextColor(...card.cor)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.text(card.value, x + cardW / 2, cardY + 13, { align: 'center' })
  })

  /* ── Tabela de dados ── */
  const colunas = [
    { header: 'ID',           dataKey: 'id' },
    { header: 'SKU',          dataKey: 'produto_sku' },
    { header: 'Descrição',    dataKey: 'descricao' },
    { header: 'Tipo',         dataKey: 'tipo' },
    { header: 'Banco Origem', dataKey: 'banco_origem' },
    { header: 'Banco Destino',dataKey: 'banco_destino' },
    { header: 'Data',         dataKey: 'data' },
    { header: 'Qtd.',         dataKey: 'quantidade' },
    { header: 'Custo',        dataKey: 'precocusto' },
  ]

  const linhas = transferencias.map((t) => ({
    id:           t.id,
    produto_sku:  t.produto_sku,
    descricao:    t.descricao,
    tipo:         t.tipo,
    banco_origem: t.banco_origem,
    banco_destino:t.banco_destino,
    data:         formatDate(t.data, 'dd/MM/yyyy HH:mm:ss'),
    quantidade:   t.quantidade,
    precocusto:   formatPrice(t.precocusto),
  }))

  autoTable(doc, {
    startY: cardY + 20,
    columns: colunas,
    body: linhas,
    margin: { left: 14, right: 14 },
    styles: {
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      overflow: 'ellipsize',
      valign: 'middle',
    },
    headStyles: {
      fillColor: azul,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'left',
    },
    columnStyles: {
      quantidade:  { halign: 'right' },
      precocusto:  { halign: 'right' },
    },
    alternateRowStyles: {
      fillColor: cinzaClaro,
    },
    /* Linha de totais no rodapé da tabela */
    foot: [[
      { content: 'TOTAIS', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold', fillColor: [220, 234, 255], textColor: azul } },
      { content: String(totalQtd), styles: { halign: 'right', fontStyle: 'bold', fillColor: [220, 234, 255], textColor: azul } },
      { content: formatPrice(totalCusto), styles: { halign: 'right', fontStyle: 'bold', fillColor: [220, 234, 255], textColor: azul } },
    ]],
    showFoot: 'lastPage',
    /* Colorir badges de tipo inline */
    didParseCell(data) {
      if (data.column.dataKey === 'tipo' && data.section === 'body') {
        const v = String(data.cell.raw).toLowerCase()
        data.cell.styles.textColor = v === 'entrada' ? verde : vermelho
        data.cell.styles.fontStyle = 'bold'
      }
    },
    /* Número de página no rodapé de cada página */
    didDrawPage(data) {
      const totalPages = doc.internal.getNumberOfPages()
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber
      doc.setFontSize(7)
      doc.setTextColor(...cinzaTexto)
      doc.text(
        `Página ${currentPage} de ${totalPages}  —  Sistema de Gestão de Transferências`,
        pageW / 2,
        doc.internal.pageSize.getHeight() - 5,
        { align: 'center' },
      )
    },
  })

  doc.save(`transferencias_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`)
}

/* ──────────────────────────── Componente principal ────────────────────────── */
export function ConsultarTransferencias() {
  const [transferencias, setTransferencias] = useState([])
  const [databases, setDatabases] = useState([])

  const [isTransferenciasLoading, setIsTransferenciasLoading] = useState(true)
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(true)
  const [isLoadingPDFGenerate, setIsLoadingPDFGenerate] = useState(false)

  const [bancoOrigem, setBancoOrigem] = useState('')
  const [bancoDestino, setBancoDestino] = useState('')
  const [tipo, setTipo] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const [itemOffset, setItemOffset] = useState(0)
  const [itemsPerPage] = useState(10)
  const endOffset = itemOffset + itemsPerPage
  const [pageCount, setPageCount] = useState(0)

  const databasesOptions = databases.map((db) => ({
    label: db.nome_conta,
    value: db.id_conta,
  }))

  const { sortedArray: sortedTransferencias, check, handleOrganize, orderType } =
    useOrderTable(transferencias, 'id')

  function handlePageClick(event) {
    const newOffset = (event.selected * itemsPerPage) % transferencias.length
    setItemOffset(newOffset)
  }

  /* ── Filtragem completa (sem paginação) ── */
  const allFiltered = useMemo(() => {
    let filtered = [...sortedTransferencias]

    if (bancoOrigem)
      filtered = filtered.filter((t) => t.banco_origem === bancoOrigem.label)
    if (bancoDestino)
      filtered = filtered.filter((t) => t.banco_destino === bancoDestino.label)

    filtered = filtered.filter((t) =>
      String(t.tipo).toLowerCase().includes(String(tipo).toLowerCase()),
    )

    if (startDate && endDate) {
      const start = startOfDay(parseISO(startDate))
      const end = startOfDay(parseISO(endDate))
      if (isValid(start) && isValid(end) && !isAfter(start, end)) {
        filtered = filtered.filter((t) =>
          isWithinInterval(startOfDay(parseISO(t.data)), { start, end }),
        )
      }
    }

    return filtered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bancoDestino, bancoOrigem, endDate, sortedTransferencias, startDate, tipo, orderType.field, orderType.type])

  /* ── Fatia paginada ── */
  const pageSlice = useMemo(() => {
    setPageCount(Math.ceil(allFiltered.length / itemsPerPage))
    return allFiltered.slice(itemOffset, endOffset)
  }, [allFiltered, endOffset, itemOffset, itemsPerPage])

  /* ── Totais da seleção ── */
  const totalCosts = useMemo(
    () => allFiltered.reduce((a, t) => a + (t.precocusto || 0), 0),
    [allFiltered],
  )
  const totalQuantidade = useMemo(
    () => allFiltered.reduce((a, t) => a + (t.quantidade || 0), 0),
    [allFiltered],
  )

  /* ── Rótulos de filtro ── */
  const periodoLabel = useMemo(
    () => buildPeriodoLabel(startDate, endDate),
    [startDate, endDate],
  )
  const filtrosAtivos = [
    bancoOrigem ? `Origem: ${bancoOrigem.label}` : null,
    bancoDestino ? `Destino: ${bancoDestino.label}` : null,
    tipo ? `Tipo: ${tipo}` : null,
    periodoLabel !== 'Todos os períodos' ? periodoLabel : null,
  ].filter(Boolean)

  /* ── Handlers ── */
  function handleClearFilter() {
    setBancoOrigem('')
    setBancoDestino('')
    setStartDate('')
    setEndDate('')
    setTipo('')
  }

  async function loadTransferencias() {
    setIsTransferenciasLoading(true)
    try {
      setTransferencias(await HistoricoTransferenciaService.getAll())
    } catch (err) {
      console.error(err)
      toast.error('Erro ao carregar as transferências!')
    } finally {
      setIsTransferenciasLoading(false)
    }
  }

  async function loadDatabases() {
    setIsLoadingDatabases(true)
    try {
      setDatabases(await EmpresasDatabaseService.getAll())
    } catch (err) {
      console.error(err)
      toast.error('Erro ao pegar os bancos de dados da empresa!')
    } finally {
      setIsLoadingDatabases(false)
    }
  }

  async function handleGerarRelatorio() {
    if (allFiltered.length === 0) return
    setIsLoadingPDFGenerate(true)
    try {
      gerarRelatorioPDF({ transferencias: allFiltered, filtrosAtivos, periodoLabel })
    } catch (err) {
      console.error(err)
      toast.error('Erro ao gerar o PDF, tente novamente mais tarde!')
    } finally {
      setIsLoadingPDFGenerate(false)
    }
  }

  useEffect(() => {
    loadTransferencias()
    loadDatabases()
  }, [])

  /* ── Render ── */
  return (
    <>
      <Navbar />

      <div className="mt-page">
        <h1>Consultar Transferências</h1>

        {/* Filtro de bancos */}
        <Row className="mb-2 justify-content-center">
          <Col xs={4}>
            <Form.Group>
              <Form.Label>Banco de Origem</Form.Label>
              <Select
                onChange={(opt) => { setBancoDestino(''); setBancoOrigem(opt) }}
                placeholder={isLoadingDatabases ? 'Carregando...' : 'Selecione um banco'}
                isDisabled={isLoadingDatabases}
                value={bancoOrigem}
                options={databasesOptions}
              />
            </Form.Group>
          </Col>

          <Col xs={4}>
            <Form.Group>
              <Form.Label>Banco de Destino</Form.Label>
              <Select
                placeholder={isLoadingDatabases ? 'Carregando...' : 'Selecione um banco'}
                value={bancoDestino}
                onChange={(opt) => setBancoDestino(opt)}
                isDisabled={isLoadingDatabases}
                options={databasesOptions.filter((d) => d.value !== bancoOrigem?.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Filtros secundários */}
        <Row className="align-items-end mb-3">
          <Col>
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Form.Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="">Ambos</option>
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col>
            <Form.Label>De:</Form.Label>
            <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Col>
          <Col>
            <Form.Label>Até:</Form.Label>
            <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Col>
          <Col className="d-flex">
            <CustomButton title="Limpar Filtro" color="#e62222" onClick={handleClearFilter}>
              <XCircleFill />
            </CustomButton>
          </Col>
        </Row>

        {/* Conteúdo */}
        {isTransferenciasLoading ? (
          <div className="d-flex align-items-center justify-content-center p-4">
            <CustomSpinner />
          </div>
        ) : transferencias.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center w-100">
            <div className="d-flex flex-column align-items-start">
              <h1 style={{ fontSize: 24 }}>Sem transferências encontradas!</h1>
              <CustomButton title="Tentar Novamente" styles={{ marginLeft: 0 }} onClick={loadTransferencias}>
                <ArrowClockwise />
              </CustomButton>
            </div>
            <img src={noFoundItemsImg} style={{ width: 400 }} alt="Nenhum item" />
          </div>
        ) : (
          <div>
            {/* Resumo rápido */}
            <div
              className="d-flex gap-4 mb-3 p-3"
              style={{ background: '#f8f9fa', borderRadius: 8, fontSize: 14 }}
            >
              <span><strong>{allFiltered.length}</strong> transferência(s) encontrada(s)</span>
              <span>Quantidade total: <strong>{totalQuantidade}</strong></span>
              <span>Custo total: <strong>{formatPrice(totalCosts)}</strong></span>
            </div>

            <Table striped hover responsive>
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('id')} style={thStyle}>
                    ID <CaretUp color={check('id', 'asc')} /><CaretDown color={check('id', 'desc')} />
                  </th>
                  <th onClick={() => handleOrganize('produto_sku')} style={thStyle}>
                    SKU <CaretUp color={check('produto_sku', 'asc')} /><CaretDown color={check('produto_sku', 'desc')} />
                  </th>
                  <th>Descrição</th>
                  <th>Tipo</th>
                  <th>Banco Origem</th>
                  <th>Banco Destino</th>
                  <th onClick={() => handleOrganize('data')} style={thStyle}>
                    Data <CaretUp color={check('data', 'asc')} /><CaretDown color={check('data', 'desc')} />
                  </th>
                  <th onClick={() => handleOrganize('quantidade')} style={thStyle}>
                    Qtd. <CaretUp color={check('quantidade', 'asc')} /><CaretDown color={check('quantidade', 'desc')} />
                  </th>
                  <th>Custo</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pageSlice.map((transferencia) => (
                  <Transferencia
                    key={transferencia.id}
                    transferencia={transferencia}
                    loadTransferencias={loadTransferencias}
                  />
                ))}
              </tbody>
            </Table>

            <CustomButton
              title="Gerar Relatório PDF"
              color="#9322e6"
              onClick={handleGerarRelatorio}
              isLoading={isLoadingPDFGenerate}
              isDisabled={allFiltered.length === 0}
            >
              <FiletypePdf />
            </CustomButton>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Próxima"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="Anterior"
              renderOnZeroPageCount={null}
              containerClassName="pagination"
              activeClassName="active"
            />
          </div>
        )}
      </div>
    </>
  )
}

const thStyle = { cursor: 'pointer', userSelect: 'none' }

/* ──────────────────────────── Linha da tabela ──────────────────────────── */
function Transferencia({ transferencia, loadTransferencias }) {
  const [isDeleting, setIsDeleting] = useState(false)

  async function handleDelete(id) {
    setIsDeleting(true)
    try {
      await HistoricoTransferenciaService.delete(id)
      toast.success('Transferência deletada com sucesso!')
      loadTransferencias()
    } catch (error) {
      console.error(error)
      toast.error('Erro ao deletar a transferência, tente novamente mais tarde!')
    } finally {
      setIsDeleting(false)
    }
  }

  const tipoColor = transferencia.tipo === 'entrada' ? '#198754' : '#dc3545'

  return (
    <tr>
      <td>{transferencia.id}</td>
      <td>{transferencia.produto_sku}</td>
      <td>{transferencia.descricao}</td>
      <td>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
            background: tipoColor + '1a',
            color: tipoColor,
            textTransform: 'capitalize',
          }}
        >
          {transferencia.tipo}
        </span>
      </td>
      <td>{transferencia.banco_origem}</td>
      <td>{transferencia.banco_destino}</td>
      <td>{formatDate(transferencia.data, 'dd/MM/yyyy, HH:mm:ss')}</td>
      <td>{transferencia.quantidade}</td>
      <td>{formatPrice(transferencia.precocusto)}</td>
      <td>
        <OverlayTrigger overlay={<Tooltip>Deletar</Tooltip>}>
          <CustomButton
            color="#e62222"
            onClick={() => handleDelete(transferencia.id)}
            isLoading={isDeleting}
          >
            <TrashFill />
          </CustomButton>
        </OverlayTrigger>
      </td>
    </tr>
  )
}